"use client"

import { useState } from "react"
import { AlertTriangle, X } from "lucide-react"

import styles from "../css/driver.module.css"

// Mirrors FAILURE_REASONS in server/controllers/driver/driverDeliveryUtils.js.
export const FAILURE_REASONS = [
  { value: "not_home", label: "Patient not home" },
  { value: "refused", label: "Patient refused delivery" },
  { value: "wrong_address", label: "Address incorrect or not found" },
  { value: "access_denied", label: "Could not access the property" },
  { value: "unsafe", label: "Unsafe to deliver" },
  { value: "other", label: "Other" },
]

const MAX_NOTE_LENGTH = 500

/**
 * Records why a delivery could not be made.
 *
 * Marking a stop failed is not reversible from the driver app — it hands the
 * delivery back to the dispensary — so the reason is required and "Other"
 * additionally requires a note.
 */
export default function FailureDialog({ onCancel, onConfirm, submitting }) {
  const [reason, setReason] = useState(null)
  const [note, setNote] = useState("")

  const noteRequired = reason === "other"
  const canSubmit = Boolean(reason) && (!noteRequired || note.trim().length > 0) && !submitting

  return (
    <div className={styles.dialogBackdrop} role="dialog" aria-modal="true" aria-labelledby="fail-title">
      <div className={styles.dialog}>
        <div className={styles.dialogHeader}>
          <h2 className={styles.dialogTitle} id="fail-title">
            <AlertTriangle size={18} aria-hidden="true" />
            Can&apos;t deliver
          </h2>
          <button
            type="button"
            className={styles.iconBtn}
            onClick={onCancel}
            disabled={submitting}
            aria-label="Close"
          >
            <X size={18} aria-hidden="true" />
          </button>
        </div>

        <p className={styles.dialogBody}>
          This returns the delivery to the dispensary and moves you on to your next stop. You
          can&apos;t undo it from here.
        </p>

        <div className={styles.field}>
          <span className={styles.fieldLabel} id="fail-reason-label">
            Reason
          </span>
          <div className={styles.reasonList} role="radiogroup" aria-labelledby="fail-reason-label">
            {FAILURE_REASONS.map((option) => (
              <div className={styles.reasonOption} key={option.value}>
                <input
                  type="radio"
                  id={`reason-${option.value}`}
                  name="failureReason"
                  value={option.value}
                  checked={reason === option.value}
                  disabled={submitting}
                  onChange={() => setReason(option.value)}
                />
                <label className={styles.reasonLabel} htmlFor={`reason-${option.value}`}>
                  {option.label}
                </label>
              </div>
            ))}
          </div>
        </div>

        <div className={styles.field}>
          <label className={styles.fieldLabel} htmlFor="failure-note">
            Note {noteRequired ? "(required)" : "(optional)"}
          </label>
          <textarea
            id="failure-note"
            className={styles.textarea}
            rows={3}
            maxLength={MAX_NOTE_LENGTH}
            placeholder="Anything the dispensary needs to know"
            value={note}
            disabled={submitting}
            onChange={(e) => setNote(e.target.value)}
          />
        </div>

        <button
          type="button"
          className={styles.dangerBtn}
          disabled={!canSubmit}
          onClick={() => onConfirm({ reason, note: note.trim() })}
        >
          {submitting ? "Recording…" : "Mark as undelivered"}
        </button>

        <button
          type="button"
          className={styles.secondaryBtn}
          onClick={onCancel}
          disabled={submitting}
        >
          Go back
        </button>
      </div>
    </div>
  )
}
